import React, { useState } from 'react';
import { makeStyles, Theme, createStyles, ClassNameMap } from "@mui/styles";
import { Select, MenuItem } from '@mui/material';
import { Toolbar, Typography, IconButton } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import CookieManager from "~common/lib/cookie-manager";

const useStyles = makeStyles((theme: Theme)=>
createStyles({
    container: {
        fontSize: '0.75rem'
      },
      pager: {
        display: 'flex',
        flexDirection: 'row-reverse'
      },
      spacer: {
        width: 15 
      },
      toolbar: {
        justifyContent:"flex-end"
      },
      formField: {
        width: '100%'
      },
      /* Styles applied to the Select component `root` class. */
      selectRoot: {
        fontSize: theme.typography.pxToRem(12),
        paddingRight: 10,
        paddingLeft: 10,
        color: theme.palette.text.secondary
      },
    
      /* Styles applied to the Select component `select` class. */
      select: {
        paddingLeft: 8,
        paddingRight: 12,
      },
    
      /* Styles applied to the Select component `icon` class. */
      selectIcon: {
        top: 1
      }
})
);

interface PagerProps {
  federatedPageLink?: Array<object>;
  /**
   * The total number of rows.
   */
  count: number;

  /**
   * The zero-based index of the current page.
   */
  page: number;

  /**
   * Callback fired when the page is changed.
   *
   * @param {object} event The event source of the callback
   * @param {number} page The page selected
   */
  onChangePage: Function;

  /**
   * Callback fired when the number of rows per page is changed.
   *
   * @param {object} event The event source of the callback
   */
  onChangeRowsPerPage?: Function;

  /**
   * The number of rows per page.
   */
  pageSize?: number;

  /**
   * Customizes the options of the rows per page select field. If less than two options are
   * available, no select field will be displayed. options should use {label: '', value: 5} label value object.
   */
  pageSizeOptions?: Array<object>;

  /**
   * Customize the rows per page label. Invoked with a `{ from, to, count, page }`
   * object.
   */
  labelPageSize?: (object) => any;

  /**
   * Customize how the paging positioning display (eg. Page x of t (z items))
   */
  labelDisplayedItems?: (
    fromIdx: number,
    toIdx: number,
    count: number,
    page: number
  ) => any;

  /**
   * Properties applied to the next arrow [`IconButton`](/api/icon-button) element.
   */
  nextIconButtonProps?: object;

  /**
   * Properties applied to the back arrow [`IconButton`](/api/icon-button) component.
   */
  backIconButtonProps?: object;

  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css-api) below for more details.
   */
  classes?: ClassNameMap;

  /**
   * If true, the scroll to top button will be hidden
   */
  disallowScrollTop?: boolean
}

type State = {
    fromIdx: number;
    toIdx: number;
    pageSizeOptions: Array<object> | null;
  };

const defaultPageSizeOptions: Array<any> = [10, 25, 50, 100];

const deriveStateFromProps = (classes,count,page,pageSize,labelPageSize,labelDisplayedItems,onChangePage,onChangeRowsPerPage,pageSizeOptions,disallowScrollTop): State => {
    pageSize = pageSize || 10;
    page = page || 0;
    count = count || 0;
   var fromIdx: number = (page) * pageSize;
   var toIdx: number = fromIdx + pageSize - 1;
   if (toIdx >= count) {
     toIdx = count - 1;
   }

   const state: State = {
     fromIdx: fromIdx,
     toIdx: toIdx,
     pageSizeOptions:
       pageSizeOptions == undefined
         ? defaultPageSizeOptions
         : pageSizeOptions
   };

   return state;
 };

export const Pager: React.FC<PagerProps> = ({
    classes,
    count,
    page,
    pageSize,
    labelPageSize,
    labelDisplayedItems,
    onChangePage,
    onChangeRowsPerPage,
    pageSizeOptions,
    disallowScrollTop,
    federatedPageLink,
    resultCount,
  })=> {

  const myClasses = useStyles()
//   const {myClasses}: {classes: ClassNameMap} = xClasses;

  const [state, setState] = useState<State>(deriveStateFromProps(classes,
    count,
    page,
    pageSize,
    labelPageSize,
    labelDisplayedItems,
    onChangePage,
    onChangeRowsPerPage,
    pageSizeOptions,
    resultCount,
    disallowScrollTop));

  // useEffect hook to update the state when props change
  React.useEffect(() => {
    setState(deriveStateFromProps(classes,
        count,
        page,
        pageSize,
        labelPageSize,
        labelDisplayedItems,
        onChangePage,
        onChangeRowsPerPage,
        pageSizeOptions,
        disallowScrollTop));
  }, [classes,
    count,
    page,
    pageSize,
    labelPageSize,
    labelDisplayedItems,
    onChangePage,
    onChangeRowsPerPage,
    pageSizeOptions,
    disallowScrollTop]);

//   const deriveStateFromProps = (classes,count,page,pageSize,labelPageSize,labelDisplayedItems,onChangePage,onChangeRowsPerPage,pageSizeOptions,disallowScrollTop): State => {
//      pageSize = pageSize || 10;
//      page = page || 0;
//      count = count || 0;
//     var fromIdx: number = (page) * pageSize;
//     var toIdx: number = fromIdx + pageSize - 1;
//     if (toIdx >= count) {
//       toIdx = count - 1;
//     }

//     const state: State = {
//       fromIdx: fromIdx,
//       toIdx: toIdx,
//       pageSizeOptions:
//         pageSizeOptions == undefined
//           ? defaultPageSizeOptions
//           : pageSizeOptions
//     };

//     return state;
//   };

  const handleChangePage = (evt: any, newPage: number) => {
    onChangePage(newPage, pageSize);
  };
  
  const handleChangePageSize = (evt: any) => {
    var ps = CookieManager.setCookie(
      `queryListPageSizeInit|${window.location.pathname}`,
      evt.target.value,
      30
    );

    onChangePage(page, evt.target.value);
  };

  const defaultLabelDisplayedItems = (
    fromIdx: number,
    toIdx: number,
    itemCount: number,
    page: number
  ): any => {
    return (
        <Typography variant="caption">{`Showing ${fromIdx + 1} to ${toIdx +
          1} of ${itemCount}`}</Typography>
      );
  };

  const lps: any = labelPageSize ? labelPageSize({}) : 'Items per page';
  const display: any = labelDisplayedItems
    ? labelDisplayedItems(state.fromIdx, state.toIdx, count, page)
    : defaultLabelDisplayedItems(state.fromIdx, state.toIdx, count, page);

  // This will overrride the "of more than" label when using federated paging
  var labelDisplayedRowsFederated = (pageData:any)=>{
    return `${pageData.from}–${pageData.to} of Federated Search Results`
  }
  // This is the default display
  var labelDisplayedRowsFN = (pageData:any)=>{
    return `${pageData.from}–${pageData.to} of ${pageData.count}`
  } 

  var disabledNext = resultCount < pageSize;
  var reportedCount = count;
  if (federatedPageLink && federatedPageLink.length > 0) {
    reportedCount = -1;
  }

  var reportedLabelDisplayedRows = labelDisplayedRowsFN;
  if (federatedPageLink && federatedPageLink.length > 0) {
    reportedLabelDisplayedRows = labelDisplayedRowsFederated;
  }

  return (
    <div className={myClasses.container}>
      <Toolbar className={myClasses.toolbar}>
        <div className={myClasses.spacer}></div>
        <div className={myClasses.spacer}></div>
        <div className={myClasses.spacer}></div>
        <table border={0}>
          <tbody>
            <tr>
              <TablePagination
                className={myClasses.actions}
                count={reportedCount}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangePageSize}
                labelRowsPerPage={lps}
                page={page}
                rowsPerPage={pageSize || 10}
                rowsPerPageOptions={pageSizeOptions || defaultPageSizeOptions}
                nextIconButtonProps={{disabled: disabledNext}}
                labelDisplayedRows={reportedLabelDisplayedRows}
              />
              <td>
                {!disallowScrollTop && (
                  <IconButton
                    onClick={() => {
                      window.scrollTo(0, 0);
                    }}
                  >
                    <i
                      style={{ fontSize: 'small' }}
                      title="Scroll to top"
                      className="fa fa-xs fa-chevron-double-up"
                    />
                  </IconButton>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </Toolbar>
    </div>
  );
};

export default Pager;
